/**
 * Shipping threshold notice
 * Updates banner copy from cart totals without remounting / re-animating on every cart change.
 */
class ShippingThreshold {
  constructor() {
    if (window.shippingThreshold instanceof ShippingThreshold) {
      return window.shippingThreshold;
    }

    const firstNotice = document.querySelector(".shipping-notice");
    this.thresholds = {
      midTier: firstNotice
        ? parseInt(firstNotice.dataset.midThreshold, 10) || 19
        : 19,
      freeShipping: firstNotice
        ? parseInt(firstNotice.dataset.freeThreshold, 10) || 75
        : 75,
    };

    this.formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });

    this._lastCartTotal = null;
    this._cartFetch = null;
    this._syncTimer = null;
    this._followUpTimer = null;
    this._announceTimer = null;
    this._announcePending = false;
    this._lastMessageText = null;

    window.shippingThreshold = this;
    this.init();
  }

  init() {
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
    }

    // Empty cart pages have no notice — don't cache a $0 total from /cart.js.
    if (!document.querySelector(".shipping-notice")) return;

    this.sync({ animate: true });
  }

  handleCartUpdate(event) {
    const cart = event?.cart || event?.detail?.cart || null;
    // /cart/add.js returns a line item (no cart.total_price). /cart/change.js returns the cart.
    const cartTotalCents =
      cart?.total_price ??
      null;

    this.queueSync({
      cartTotalCents,
      animate: false,
      preferNetwork: cartTotalCents == null,
    });
    this.scheduleAnnouncement();
  }

  getActiveMessageText() {
    const message = document.querySelector(
      ".shipping-notice .shipping-notice__message.active"
    );
    return message ? message.textContent.replace(/\s+/g, " ").trim() : null;
  }

  // Waits for the section swap and network sync to settle, then announces
  // only if the visible shipping message actually changed.
  scheduleAnnouncement() {
    this._announcePending = true;
    clearTimeout(this._announceTimer);
    this._announceTimer = setTimeout(() => {
      this._announcePending = false;
      const text = this.getActiveMessageText();
      if (!text || text === this._lastMessageText) return;
      this._lastMessageText = text;
      this.announce(text);
    }, 700);
  }

  announce(text) {
    const drawer = document.querySelector("cart-drawer.active #CartDrawer");
    const container =
      drawer || document.getElementById("main-cart-footer") || document.body;

    let status = container.querySelector(".js-shipping-status-announcer");
    if (!status) {
      status = document.createElement("p");
      status.className = "visually-hidden js-shipping-status-announcer";
      status.setAttribute("role", "status");
      container.appendChild(status);
    }
    status.textContent = "";
    setTimeout(() => {
      status.textContent = text;
    }, 150);
  }

  queueSync(options = {}) {
    this.markNoticesReady(document, { keepVisible: true });

    // Paint immediately from Liquid data-cart-total (correct after section swap).
    this.applyDomTotals({ animate: false });

    this.sync({
      ...options,
      animate: false,
      preferNetwork: options.preferNetwork ?? options.cartTotalCents == null,
    });

    cancelAnimationFrame(this._syncTimer);
    this._syncTimer = requestAnimationFrame(() => {
      this.markNoticesReady(document, { keepVisible: true });
      this.applyDomTotals({ animate: false });
      this.sync({
        ...options,
        animate: false,
        preferNetwork: options.preferNetwork ?? options.cartTotalCents == null,
      });
    });
  }

  /**
   * Call immediately after cart drawer / cart section HTML is replaced
   * so the new notice never starts at opacity 0.
   */
  markNoticesReady(root = document, { keepVisible = true } = {}) {
    root.querySelectorAll(".shipping-notice").forEach((notice) => {
      if (keepVisible) {
        notice.classList.add("loaded", "no-entrance-anim");
      }
    });
  }

  /**
   * Use data-cart-total from freshly rendered section HTML.
   * This is the correct total right after first add-to-cart (before /cart.js returns).
   */
  readTotalFromNotices() {
    const notices = document.querySelectorAll(".shipping-notice");
    for (const notice of notices) {
      const total = parseFloat(notice.dataset.cartTotal);
      if (!Number.isNaN(total)) return total;
    }
    return null;
  }

  applyDomTotals({ animate = false } = {}) {
    const total = this.readTotalFromNotices();
    if (total == null || total <= 0) return false;

    this._lastCartTotal = total;
    this.updateShippingNotices({ animate, cartTotal: total });
    return true;
  }

  async sync({
    cartTotal,
    cartTotalCents,
    animate = true,
    preferNetwork = false,
  } = {}) {
    let total = cartTotal;

    if (total == null && cartTotalCents != null && !Number.isNaN(Number(cartTotalCents))) {
      total = Number(cartTotalCents) / 100;
    }

    // Prefer fresh Liquid data-cart-total over a stale cached empty-cart $0.
    if (total == null) {
      const domTotal = this.readTotalFromNotices();
      if (domTotal != null) total = domTotal;
    }

    // Never reuse a cached $0 from an empty cart when the banner is visible.
    if (
      total == null &&
      !preferNetwork &&
      this._lastCartTotal != null &&
      this._lastCartTotal > 0
    ) {
      total = this._lastCartTotal;
    }

    // Instant correct paint when we already know a positive total.
    if (total != null && total > 0) {
      this._lastCartTotal = total;
      this.updateShippingNotices({ animate, cartTotal: total });

      if (!preferNetwork) return;

      // Optional background confirm (e.g. after add.js which lacks total_price).
      const fetched = await this.fetchCartTotal();
      if (fetched != null && fetched !== total) {
        this._lastCartTotal = fetched;
        this.updateShippingNotices({ animate: false, cartTotal: fetched });
      }
      return;
    }

    if (total == null || preferNetwork) {
      const fetched = await this.fetchCartTotal();
      if (fetched != null) {
        // Ignore empty-cart fetches when a notice is already showing a positive DOM total.
        const domTotal = this.readTotalFromNotices();
        if (fetched === 0 && domTotal != null && domTotal > 0) {
          this._lastCartTotal = domTotal;
          this.updateShippingNotices({ animate: false, cartTotal: domTotal });
          return;
        }

        this._lastCartTotal = fetched > 0 ? fetched : null;
        this.updateShippingNotices({ animate: false, cartTotal: fetched });
        return;
      }
    }

    this.updateShippingNotices({ animate });
  }

  fetchCartTotal() {
    if (this._cartFetch) return this._cartFetch;

    const cartUrl = `${window.routes?.cart_url || "/cart"}.js`;
    this._cartFetch = fetch(cartUrl)
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) return null;
        return (cart.total_price || 0) / 100;
      })
      .catch(() => null)
      .finally(() => {
        this._cartFetch = null;
      });

    return this._cartFetch;
  }

  updateShippingNotices({ animate = true, cartTotal } = {}) {
    document.querySelectorAll(".shipping-notice").forEach((notice) => {
      this.updateNotice(notice, { animate, cartTotal });
    });
    if (!this._announcePending) {
      this._lastMessageText = this.getActiveMessageText();
    }
  }

  updateNotice(notice, { animate = true, cartTotal } = {}) {
    if (!notice) return;

    if (cartTotal != null && !Number.isNaN(cartTotal)) {
      notice.dataset.cartTotal = String(cartTotal);
    }

    const total = cartTotal != null ? cartTotal : this.getCartTotal(notice);

    const mid = parseInt(notice.dataset.midThreshold, 10);
    const free = parseInt(notice.dataset.freeThreshold, 10);
    if (!Number.isNaN(mid)) this.thresholds.midTier = mid;
    if (!Number.isNaN(free)) this.thresholds.freeShipping = free;

    const messages = notice.querySelectorAll(".shipping-notice__message");
    messages.forEach((msg) => msg.classList.remove("active"));

    if (!total || total === 0) {
      this.activateMessage(notice, ".save-shipping", this.thresholds.midTier - total);
    } else if (total < this.thresholds.midTier) {
      this.activateMessage(notice, ".save-shipping", this.thresholds.midTier - total);
    } else if (total < this.thresholds.freeShipping) {
      this.activateMessage(notice, ".needs-more", this.thresholds.freeShipping - total);
    } else {
      this.activateMessage(notice, ".free-shipping");
    }

    this.updateProgress(notice, total);
    this.updateProgressText(notice);

    if (!animate) {
      notice.classList.add("no-entrance-anim");
    } else {
      notice.classList.remove("no-entrance-anim");
    }

    notice.classList.add("loaded");
    this.handleSpecialCases(notice);
  }

  updateProgress(notice, cartTotal) {
    const progress = notice.querySelector(".shipping-notice__progress");
    const fill = notice.querySelector(".shipping-notice__progress-fill");
    if (!progress || !fill) return;

    const free = this.thresholds.freeShipping || 75;
    const total = Number(cartTotal) || 0;
    const percent = free > 0 ? Math.min(100, Math.max(0, (total / free) * 100)) : 0;

    fill.style.setProperty("--shipping-progress", `${percent}%`);
    progress.setAttribute("aria-valuenow", String(Math.round(percent)));
  }

  updateProgressText(notice) {
    const progress = notice.querySelector(".shipping-notice__progress");
    const message = notice.querySelector(".shipping-notice__message.active");
    if (!progress || !message) return;
    progress.setAttribute("aria-valuetext", message.textContent.trim());
  }

  activateMessage(notice, selector, amountNeeded) {
    const message = notice.querySelector(selector);
    if (!message) return;

    if (amountNeeded != null) {
      const amountElement = message.querySelector(".amount-needed");
      if (amountElement) {
        amountElement.textContent = this.formatter.format(Math.max(0, amountNeeded));
      }
    }

    message.classList.add("active");
  }

  getCartTotal(notice) {
    const total = parseFloat(notice.dataset.cartTotal);
    if (!Number.isNaN(total)) return total;

    if (this._lastCartTotal != null && this._lastCartTotal > 0) {
      return this._lastCartTotal;
    }

    const totalElements = [
      document.querySelector(".totals__subtotal-value"),
      document.querySelector("[data-cart-total]"),
      document.querySelector(".cart-total"),
    ];

    for (const element of totalElements) {
      if (!element) continue;
      const text = element.textContent || element.innerText;
      const match = text.match(/[\d,]+\.?\d*/);
      if (match) {
        return parseFloat(match[0].replace(/,/g, ""));
      }
    }

    return 0;
  }

  handleSpecialCases(notice) {
    const mixNMatchCoupons = document.querySelectorAll(".coupon-mixnmatch");
    if (mixNMatchCoupons.length > 0) {
      notice.classList.remove("loaded");
    }
  }
}

function initShippingThreshold() {
  if (!(window.shippingThreshold instanceof ShippingThreshold)) {
    new ShippingThreshold();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initShippingThreshold, { once: true });
} else {
  initShippingThreshold();
}
